/**
 * useHardwareBackHandler — Capacitor App.backButton listener.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decision D4 + Phase 4 main
 * back policy.
 *
 * Resolves every Android hardware back press through the back resolver, so
 * the same priority list (dialog → entry → activity → main → exit) governs
 * both in-app back actions and hardware back.
 *
 * Web preview: no-op (Capacitor not present). Browser/iOS swipe-back is
 * handled by RR popstate → usePopstateSync.
 *
 * Mount once at RootLayout.
 */

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";
import { dispatchBackAction } from "./dispatchBack";
import { resolveBack } from "./resolveBack";

interface RemovableListener {
  remove?: () => void | Promise<void>;
}

export function useHardwareBackHandler(): void {
  const navigate = useNavigate();
  const location = useLocation();

  // Refs let us register the listener exactly once while still reading the
  // latest location/navigate inside the callback.
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let handle: RemovableListener | null = null;
    let cancelled = false;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const listener = await App.addListener("backButton", () => {
          const stack = useActivityStore.getState();
          const action = resolveBack({
            stack: { activities: stack.activities, dialogs: stack.dialogs },
            source: "hardware",
            mainPathname: locationRef.current.pathname,
          });
          const underlying =
            stack.activities.length >= 2
              ? stack.activities[stack.activities.length - 2]
              : null;
          dispatchBackAction(
            action,
            navigateRef.current,
            underlying,
            locationRef.current.state,
          );
        });
        if (cancelled) {
          await listener.remove();
        } else {
          handle = listener as RemovableListener;
        }
      } catch {
        // Not running in Capacitor (web preview) — silently no-op.
      }
    })();

    return () => {
      cancelled = true;
      void handle?.remove?.();
    };
  }, []);
}
